import { TID } from "@/constants/testIds";

/**
 * Single trade card row inside the Day Entry modal.
 * Includes the input grid + region (good/bad) toggle and optional summary preview.
 */
export function TradeCard({ trade, index, onUpdate, onRemove, accounts = [] }) {
  const t = trade;
  const i = index;
  const setField = (field, val) => onUpdate(i, field, val);

  return (
    <div className="border border-[#2E2A25] bg-[#0F0E0C] p-3 rounded-sm space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <input
          data-testid={TID.tradePair(i)}
          placeholder="EURUSD"
          value={t.pair}
          onChange={(e) => setField("pair", e.target.value.toUpperCase())}
          className="col-span-2 md:col-span-1 bg-[#1A1815] border border-[#2E2A25] text-[#EBE5D9] px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm"
        />
        <select
          data-testid={TID.tradeDirection(i)}
          value={t.direction}
          onChange={(e) => setField("direction", e.target.value)}
          className="bg-[#1A1815] border border-[#2E2A25] text-[#EBE5D9] px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm"
        >
          <option value="long">LONG</option>
          <option value="short">SHORT</option>
        </select>
        <input
          data-testid={TID.tradeLot(i)}
          type="number"
          step="0.01"
          placeholder="Lots"
          value={t.lot_size}
          onChange={(e) => setField("lot_size", e.target.value)}
          className="bg-[#1A1815] border border-[#2E2A25] text-[#EBE5D9] px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm"
        />
        <input
          data-testid={TID.tradeEntry(i)}
          type="number"
          step="0.00001"
          placeholder="Entry"
          value={t.entry}
          onChange={(e) => setField("entry", e.target.value)}
          className="bg-[#1A1815] border border-[#2E2A25] text-[#EBE5D9] px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm"
        />
        <input
          data-testid={TID.tradeExit(i)}
          type="number"
          step="0.00001"
          placeholder="Exit"
          value={t.exit}
          onChange={(e) => setField("exit", e.target.value)}
          className="bg-[#1A1815] border border-[#2E2A25] text-[#EBE5D9] px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm"
        />
        <input
          data-testid={TID.tradePnl(i)}
          type="number"
          step="0.01"
          placeholder="P&L"
          value={t.pnl}
          onChange={(e) => setField("pnl", e.target.value)}
          className={`bg-[#1A1815] border border-[#2E2A25] px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm ${
            parseFloat(t.pnl) > 0
              ? "text-[#7A9E80]"
              : parseFloat(t.pnl) < 0
                ? "text-[#B56258]"
                : "text-[#EBE5D9]"
          }`}
        />
      </div>
      <div className="flex items-center justify-between mt-2 gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          {accounts.length > 0 && (
            <select
              data-testid={`trade-account-${i}`}
              value={t.account_id || ""}
              onChange={(e) => setField("account_id", e.target.value || null)}
              className="bg-[#1A1815] border border-[#2E2A25] text-[#EBE5D9] px-2 py-1 text-xs font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm max-w-[180px]"
            >
              <option value="">Unassigned</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
          )}
          {t.outcome && (
            <span className={`font-mono text-[10px] uppercase tracking-widest ${
              t.outcome === "win" ? "text-[#7A9E80]" : t.outcome === "loss" ? "text-[#B56258]" : "text-[#5C7C8A]"
            }`}>
              {t.outcome}
            </span>
          )}
          <div className="flex items-center gap-1">
            <button
              data-testid={TID.tradeRegionGood(i)}
              type="button"
              onClick={() => setField("region_flag", t.region_flag === "good" ? null : "good")}
              className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm transition-colors ${
                t.region_flag === "good"
                  ? "bg-[#7A9E80] border-[#7A9E80] text-[#0F0E0C]"
                  : "border-[#2E2A25] text-[#7A9E80] hover:border-[#7A9E80]"
              }`}
            >
              Good
            </button>
            <button
              data-testid={TID.tradeRegionBad(i)}
              type="button"
              onClick={() => setField("region_flag", t.region_flag === "bad" ? null : "bad")}
              className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm transition-colors ${
                t.region_flag === "bad"
                  ? "bg-[#B56258] border-[#B56258] text-[#0F0E0C]"
                  : "border-[#2E2A25] text-[#B56258] hover:border-[#B56258]"
              }`}
            >
              Bad
            </button>
          </div>
        </div>
        <button
          data-testid={TID.removeTrade(i)}
          onClick={() => onRemove(i)}
          className="font-mono text-[10px] uppercase tracking-widest text-[#B56258] hover:text-[#8C4A42] transition-colors"
        >
          remove
        </button>
      </div>
      {t.summary && (
        <div className="pt-2 border-t border-[#2E2A25]">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8E] mb-1">
            Trade notes
          </p>
          <p className="text-xs text-[#EBE5D9] whitespace-pre-wrap leading-relaxed">
            {t.summary}
          </p>
        </div>
      )}
    </div>
  );
}
